import React, { Fragment } from 'react'
import {
	UPDATE_QUESTION_ACTIONS,
	UpdateQuestionAction,
} from '../../model/editableQuestion/actions'
import { TextImageField } from '../text-image-field/TextImageField'
import {
	ClientQuestion as Question,
	MediaObj,
} from '@mission.io/question-toolkit'
import { getImageMeta, isClozeDropdown } from '../../utils'
import UploadImage from '../upload-image/UploadImage'
import { MdClose } from 'react-icons/md'
import { FaCheckSquare } from 'react-icons/fa'
import { CgCloseO } from 'react-icons/cg'
import {
	InputGroup,
	UploadImageWrapper,
	QuestionHeader,
} from '../../assets/styles'
import 'styled-components/macro'
import { styled } from 'styled-components'
import { StudentResponse } from '../../model/general'
import OptionalMarkdown from '../Markdown/OptionalMarkdown'
import { _tempTheme } from '../../utils/tempTheme'
import classNames from 'classnames'

interface TeacherHeaderProps {
	darkMode?: boolean
	readOnly?: boolean
	label?: string
	question: Question
	questionIndex?: number
	onChange?: (action: UpdateQuestionAction) => void
	isValid?: boolean
	studentResponse?: StudentResponse | null
	inputType?: 'text' | 'textarea'
	autoFocus?: boolean
}

/**
 * A header component for teacher views.
 * In edit mode (`readOnly` is false)
 *   Displays `label` at the top, followed by an input area to edit the question/add an image.
 * @param props
 * @returns
 */
export default function TeacherHeader({
	darkMode = false,
	readOnly,
	label = 'Question',
	inputType = 'text',
	question,
	questionIndex = 0,
	onChange,
	isValid,
	studentResponse,
	autoFocus = false,
}: TeacherHeaderProps): JSX.Element {
	const questionMedia = question.media?.find(
		(mediaItem: MediaObj) => mediaItem.type === 'IMAGE'
	)
	const doesQuestionHaveImg = !!questionMedia

	return readOnly ? (
		<Fragment>
			{!isValid && (
				<Fragment>
					<div className="invalid-question-cover" />
					<p className="invalid-question-text">
						This question is in an invalid format and is not available for use.{' '}
						{question.default && 'It will not be included in your mission. '}
						Thank you for your patience while we fix this issue!
					</p>
				</Fragment>
			)}
			<QuestionHeader>
				<span>
					{!studentResponse ? (
						<strong className="notranslate">
							{`Q${questionIndex + 1}.`}&nbsp;&nbsp;
						</strong>
					) : studentResponse.answer.value &&
					  studentResponse.score &&
					  studentResponse.score?.score ===
							studentResponse.score?.perfectScore ? (
						<FaCheckSquare className="student-mark right" />
					) : (
						<CgCloseO className="student-mark wrong" />
					)}
				</span>
				<div>
					<OptionalMarkdown className={darkMode ? 'prose-invert' : ''}>
						{isClozeDropdown(question) ? question.template : question.phrase}
					</OptionalMarkdown>
				</div>
			</QuestionHeader>
		</Fragment>
	) : (
		<div className="bg-brand-cloud-blue text-brand-space-blue rounded-t-lg p-4">
			<div className="font-semibold mb-4 text-start">{label}</div>
			<InputGroup>
				<QuestionLabel>
					<span className="notranslate">{`Q${questionIndex + 1}`}</span>
				</QuestionLabel>
				<TextImageField
					className={classNames(
						'rounded-tl-none',
						inputType === 'text' && 'rounded-bl-none'
					)}
					type={inputType}
					placeholder="Type Question..."
					value={
						isClozeDropdown(question) ? question.template : question.phrase
					}
					onChange={(e: any) =>
						onChange &&
						onChange({
							type: UPDATE_QUESTION_ACTIONS.CHANGE_QUESTION,
							payload: e.target.value,
						})
					}
					media={question.media}
					onDeleteImage={() => {
						if (!readOnly && onChange) {
							onChange({
								type: UPDATE_QUESTION_ACTIONS.DELETE_QUESTION_IMAGE,
							})
						}
					}}
					autoFocus={autoFocus}
				/>
				<UploadImageWrapper $isTextInput={inputType === 'text'}>
					<UploadImage
						disabled={doesQuestionHaveImg}
						onChange={(file: File | Blob) => {
							if (!readOnly && onChange) {
								const { localUrl } = getImageMeta(file)

								onChange({
									type: UPDATE_QUESTION_ACTIONS.CHANGE_QUESTION_IMAGE,
									payload: {
										type: 'IMAGE',
										file: file,
										localUrl: localUrl,
									},
								})
							}
						}}
						readOnly={readOnly}
					/>
				</UploadImageWrapper>
			</InputGroup>
		</div>
	)
}

export const CloseIcon = styled(MdClose)`
	cursor: pointer;
	position: absolute;
	top: ${_tempTheme.spacing4};
	right: ${_tempTheme.spacing4};

	transform: scale(1.3);
	background: #e7eaf7;
	border-radius: 50%;
	color: ${({ theme }) => theme.primary};
	padding: 2px;
`

export const QuestionLabel = styled.div`
	background-color: ${({ theme }) => theme.primary};
	border-radius: ${_tempTheme.spacing1} 0 0 ${_tempTheme.spacing1};
	text-align: center;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 ${_tempTheme.spacing2};
	max-height: 36px;
	span {
		font-size: ${_tempTheme.text_base};
		line-height: 19px;
		text-align: center;
		color: white;
		width: ${_tempTheme.spacing6};
	}
`
